import React, { useState, useEffect } from 'react';
import { DynamicFieldGenerator } from '~/components/DynamicFieldGenerator';
import { FaChevronDown, FaCheckCircle } from 'react-icons/fa';
import type { CheckerSchema } from '~/resources/loyalty/stamp-template/hooks/useStampingSchemas';
import { useStampingPreviews } from '~/resources/loyalty/stamp-template/hooks/useStampingPreviews';

export interface CheckerValue {
  code: string;
  params: Record<string, any>;
}

interface CheckerSelectorProps {
  value: CheckerValue;
  onChange: (checker: CheckerValue, preview?: string) => void;
  availableCheckers: CheckerSchema[];
  error?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}

export const CheckerSelector: React.FC<CheckerSelectorProps> = ({
  value,
  onChange,
  availableCheckers,
  error,
  label = 'Condition',
  required = false,
  disabled = false
}) => {
  const [selectedChecker, setSelectedChecker] = useState<CheckerSchema | null>(null);
  const { getCheckerPreview } = useStampingPreviews({ availableCheckers });

  // Update selected checker when value changes
  useEffect(() => {
    const checker = availableCheckers.find(c => c.code === value?.code);
    setSelectedChecker(checker || null);
  }, [value?.code, availableCheckers]);

  // Handle checker type change
  const handleCheckerChange = (checkerCode: string) => {
    if (!checkerCode) {
      onChange({ code: '', params: {} });
      return;
    }

    const checker = availableCheckers.find(c => c.code === checkerCode);
    if (checker) {
      // Initialize params with defaults
      const defaultParams: Record<string, any> = {};
      Object.entries(checker.paramSchema).forEach(([key, schema]) => {
        if (schema.default !== undefined) {
          defaultParams[key] = schema.default;
        } else if (schema.required) {
          // Set sensible defaults for required fields
          if (schema.type === 'number') {
            defaultParams[key] = schema.min || 0;
          } else if (schema.type === 'string') {
            defaultParams[key] = '';
          } else if (schema.type === 'select' && schema.options?.length) {
            defaultParams[key] = schema.options[0];
          } else if (schema.type === 'boolean') {
            defaultParams[key] = false;
          }
        }
      });

      onChange({
        code: checkerCode,
        params: defaultParams
      });
    }
  };

  // Handle parameter changes
  const handleParamsChange = (params: Record<string, any>) => {
    onChange({
      ...value,
      params
    }, getCheckerPreview({ ...value, params }, selectedChecker || undefined));
  };

  return (
    <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-white hover:border-indigo-300 transition-colors">
      {/* Checker Type Selection */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <FaCheckCircle className="text-indigo-500" />
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>

        <div className="relative">
          <select
            value={value?.code || ''}
            onChange={(e) => handleCheckerChange(e.target.value)}
            disabled={disabled}
            className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors appearance-none ${error ? 'border-red-500' : 'border-gray-300'
              } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
          >
            <option value="">Select a condition...</option>
            {availableCheckers.map((checker) => (
              <option key={checker.code} value={checker.code}>
                {checker.name}
              </option>
            ))}
          </select>
          <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>

      </div>

      {/* Dynamic Parameters */}
      {selectedChecker && Object.keys(selectedChecker.paramSchema).length > 0 && (
        <div className="pl-4 border-l-2 border-indigo-200">
          <DynamicFieldGenerator
            schema={selectedChecker.paramSchema}
            values={value?.params || {}}
            onChange={handleParamsChange}
            errors={error ? { params: error } : {}}
            disabled={disabled}
          />
        </div>
      )}

      {/* Checker Preview */}
      {selectedChecker && value?.code && (
        <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
          <p className="text-sm font-medium text-indigo-700">
            <span className="text-xs uppercase tracking-wide text-indigo-500">Preview:</span>
            <br />
            {getCheckerPreview(value, selectedChecker)}
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <span>⚠️</span>
          {error}
        </p>
      )}
    </div>
  );
};

export default CheckerSelector;